import { HubPageName } from './hub-page-names.constants';

/**
 * Route pattern configuration for path → page_name resolution
 */
export interface RoutePattern {
  /** Regex pattern to match the path */
  pattern: RegExp;
  /** The standardized page name */
  pageName: HubPageName;
}

/**
 * Hub route patterns for page tracking
 *
 * ORDER MATTERS: More specific patterns must come before general patterns.
 * For example, /profile/edit must come before /profile to avoid
 * /profile/edit matching as just /profile.
 */
export const HUB_ROUTE_PATTERNS: RoutePattern[] = [
  // Authentication routes (public)
  { pattern: /^\/logout$/, pageName: HubPageName.LOGOUT },
  { pattern: /^\/register$/, pageName: HubPageName.REGISTER },
  { pattern: /^\/signup\/contest$/, pageName: HubPageName.REGISTER_CONTEST },

  // Profile routes (specific before general)
  { pattern: /^\/profile\/edit$/, pageName: HubPageName.PROFILE_EDIT },
  {
    pattern: /^\/profile\/verification$/,
    pageName: HubPageName.PROFILE_VERIFICATION,
  },
  { pattern: /^\/profile(\/[^/]*)?$/, pageName: HubPageName.PROFILE },

  // Contest routes (specific before general)
  {
    pattern: /^\/contests\/contest-overview\/[^/]+/,
    pageName: HubPageName.CONTEST_DASHBOARD,
  },
  { pattern: /^\/contests\/[^/]+$/, pageName: HubPageName.CONTEST_DETAILS },
  { pattern: /^\/contests$/, pageName: HubPageName.CONTESTS },

  // Overview/Dashboard routes (specific before general)
  {
    pattern: /^\/overview\/[^/]+\/position\/[^/]+/,
    pageName: HubPageName.POSITION,
  },
  { pattern: /^\/overview\/[^/]+/, pageName: HubPageName.PROGRAM_DASHBOARD },
  { pattern: /^\/overview$/, pageName: HubPageName.OVERVIEW },

  // Legacy overview routes (TTP uses /overview-old)
  {
    pattern: /^\/overview-old\/[^/]+/,
    pageName: HubPageName.PROGRAM_DASHBOARD,
  },

  // Purchases routes (new feature - specific before general)
  { pattern: /^\/purchases\/coupons/, pageName: HubPageName.PURCHASES_COUPONS },
  { pattern: /^\/purchases\/orders$/, pageName: HubPageName.PURCHASES_ORDERS },
  {
    pattern: /^\/purchases\/withdrawals$/,
    pageName: HubPageName.PURCHASES_WITHDRAWALS,
  },
  { pattern: /^\/purchases$/, pageName: HubPageName.PURCHASES },

  // Orders routes (legacy)
  { pattern: /^\/orders\/[^/]+$/, pageName: HubPageName.ORDERS },
  { pattern: /^\/orders$/, pageName: HubPageName.ORDERS },

  // Coupons redirect route
  { pattern: /^\/coupons\//, pageName: HubPageName.PURCHASES_COUPONS },

  // Affiliate routes
  { pattern: /^\/affiliate-kit$/, pageName: HubPageName.AFFILIATE_KIT },
  { pattern: /^\/affiliate$/, pageName: HubPageName.AFFILIATE },

  // Trade Talk routes
  {
    pattern: /^\/trade-talk\/[^/]+$/,
    pageName: HubPageName.TRADE_TALK_ARTICLE,
  },
  { pattern: /^\/trade-talk$/, pageName: HubPageName.TRADE_TALK },

  // Playbook routes
  { pattern: /^\/playbook\/[^/]+/, pageName: HubPageName.PLAYBOOK_DETAIL },
  { pattern: /^\/playbook$/, pageName: HubPageName.PLAYBOOK },

  // Purchase flow routes
  { pattern: /^\/new-account/, pageName: HubPageName.NEW_ACCOUNT },
  { pattern: /^\/new-addon\/[^/]+/, pageName: HubPageName.NEW_ADDON },
  { pattern: /^\/purchase$/, pageName: HubPageName.PURCHASE },
  { pattern: /^\/quick-purchase$/, pageName: HubPageName.QUICK_PURCHASE },
  {
    pattern: /^\/after-purchase\/[^/]+$/,
    pageName: HubPageName.AFTER_PURCHASE,
  },
  {
    pattern: /^\/paysafe-checkout\/[^/]+$/,
    pageName: HubPageName.PAYSAFE_CHECKOUT,
  },

  // Static content pages
  { pattern: /^\/achievements$/, pageName: HubPageName.ACHIEVEMENTS },
  { pattern: /^\/charts$/, pageName: HubPageName.CHARTS },
  { pattern: /^\/welcome$/, pageName: HubPageName.WELCOME },
  { pattern: /^\/partners\/[^/]+$/, pageName: HubPageName.PARTNERS },
  { pattern: /^\/privacy-opt-out$/, pageName: HubPageName.PRIVACY_OPT_OUT },
  { pattern: /^\/blacklisted$/, pageName: HubPageName.BLACKLISTED },

  // Static iframe pages
  { pattern: /^\/autochartist$/, pageName: HubPageName.AUTOCHARTIST },
  {
    pattern: /^\/assets-specifications$/,
    pageName: HubPageName.ASSETS_SPECIFICATIONS,
  },
  { pattern: /^\/hub-downloads$/, pageName: HubPageName.HUB_DOWNLOADS },
  { pattern: /^\/hub-vod$/, pageName: HubPageName.HUB_VOD },
  { pattern: /^\/trading-plan$/, pageName: HubPageName.TRADING_PLAN },

  // Root/Login (when not authenticated, root redirects to login)
  { pattern: /^\/$/, pageName: HubPageName.LOGIN },
  { pattern: /^\/login$/, pageName: HubPageName.LOGIN },
];
